import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  EMPTY_AUDIT_FILTERS,
  parseFilters,
  serializeFilters,
  validateFilters,
  type AuditLogFilters,
} from '@entities/audit-log';

const EVENT_PARAM = 'event';

export function useAuditLogFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const eventId = searchParams.get(EVENT_PARAM);
  const errors = useMemo(() => validateFilters(filters), [filters]);

  const write = useCallback(
    (next: AuditLogFilters, event: string | null) => {
      const params = serializeFilters(next);
      if (event) params.set(EVENT_PARAM, event);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const setFilters = useCallback(
    (next: AuditLogFilters) => write(next, searchParams.get(EVENT_PARAM)),
    [write, searchParams]
  );
  const openEvent = useCallback(
    (id: string) => write(parseFilters(searchParams), id),
    [write, searchParams]
  );
  const closeEvent = useCallback(
    () => write(parseFilters(searchParams), null),
    [write, searchParams]
  );
  const applyQuickFilter = useCallback(
    (patch: Partial<AuditLogFilters>) => {
      const current = parseFilters(searchParams);
      write(
        {
          ...EMPTY_AUDIT_FILTERS,
          dateFrom: current.dateFrom,
          dateTo: current.dateTo,
          ...patch,
        },
        null
      );
    },
    [write, searchParams]
  );

  return {
    filters,
    eventId,
    errors,
    isValid: errors.length === 0,
    setFilters,
    openEvent,
    closeEvent,
    applyQuickFilter,
  };
}
